import { Send, Eye, TestTube2, CalendarClock, X } from "lucide-react";
import { SafeHtml } from "@/lib/safeHtml";

/**
 * Newsletter composer block: subject, HTML body, preview, scheduler, action buttons.
 * All state is owned by useAdminNewsletter — this is a controlled presentation.
 */
const NewsletterComposer = ({
  subject, setSubject,
  bodyHtml, setBodyHtml,
  scheduledAt, setScheduledAt,
  preview, setPreview,
  loading, sendTest, sendBroadcast,
  subscribersTotal = 0,
}) => (
  <div className="bg-white rounded-3xl border border-sand/50 mt-10 overflow-hidden">
    <div className="p-7 border-b border-sand/40">
      <h2 className="font-serif text-2xl text-charcoal mb-1">Componer newsletter</h2>
      <p className="text-sm text-charcoal/60">
        Escribe en HTML simple. Usa <code className="text-terracotta">{"{name}"}</code> donde quieras que aparezca el nombre de la suscriptora.
      </p>
      <div
        className="mt-4 flex items-start gap-3 p-4 bg-terracotta/10 border border-terracotta/30 rounded-xl text-[13.5px] text-charcoal leading-relaxed"
        data-testid="warning-banner"
      >
        <span className="text-terracotta text-lg leading-none mt-0.5">!</span>
        <div>
          <strong className="text-terracotta">Antes de darle a "Enviar":</strong> el cuerpo de abajo trae una plantilla de ayuda con corchetes <code className="text-terracotta">[así]</code>. Sustituye todo el contenido por tu newsletter real. Si dejas la plantilla tal cual, eso es exactamente lo que recibirán tus suscriptoras.
        </div>
      </div>
    </div>

    <div className="p-7 space-y-5">
      <div>
        <label className="text-[11px] tracking-[0.28em] uppercase text-charcoal/60 mb-2 block">Asunto</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="No fue el cáncer. Fue lo que vino después."
          data-testid="newsletter-subject-input"
          className="w-full px-4 py-3 rounded-xl border border-sand/80 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none text-charcoal"
        />
      </div>

      <div>
        <label className="text-[11px] tracking-[0.28em] uppercase text-charcoal/60 mb-2 block">Cuerpo (HTML)</label>
        <textarea
          rows={16}
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
          data-testid="newsletter-body-textarea"
          className="w-full px-4 py-3 rounded-xl border border-sand/80 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none text-charcoal font-mono text-[13px] leading-relaxed"
        />
      </div>

      {preview && (
        <div className="border border-sand/60 rounded-xl bg-cream/50 p-5">
          <p className="text-[10px] tracking-[0.28em] uppercase text-charcoal/55 mb-3">Vista previa</p>
          <SafeHtml
            className="prose prose-sm max-w-none text-charcoal/85"
            html={bodyHtml.replace(/{name}/g, "Marta")}
          />
        </div>
      )}

      <div className="rounded-xl border border-sand/60 bg-cream/40 p-5" data-testid="schedule-block">
        <label className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-charcoal/65 mb-3">
          <CalendarClock size={14} className="text-terracotta" />
          Programar envío (opcional)
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            min={new Date(Date.now() + 5 * 60_000).toISOString().slice(0, 16)}
            data-testid="schedule-datetime-input"
            className="px-4 py-2.5 rounded-xl border border-sand/80 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none text-charcoal text-sm"
          />
          {scheduledAt && (
            <button
              type="button"
              onClick={() => setScheduledAt("")}
              data-testid="schedule-clear-btn"
              className="inline-flex items-center gap-1.5 text-xs text-charcoal/60 hover:text-terracotta transition-colors"
            >
              <X size={13} /> Limpiar
            </button>
          )}
        </div>
        <p className="text-[12.5px] text-charcoal/55 mt-2.5 leading-relaxed">
          Si dejas la fecha vacía, se enviará <strong>inmediatamente</strong>.
          Si pones fecha y hora, Resend la guarda y la envía sola en ese momento (máximo 30 días en el futuro).
        </p>
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        <button
          onClick={() => setPreview(!preview)}
          data-testid="newsletter-preview-btn"
          className="inline-flex items-center gap-2 border border-charcoal/20 text-charcoal rounded-full px-5 py-2.5 text-sm hover:border-charcoal/50 transition-colors"
        >
          <Eye size={15} /> {preview ? "Ocultar" : "Mostrar"} preview
        </button>
        <button
          onClick={sendTest}
          disabled={loading}
          data-testid="newsletter-send-test-btn"
          className="inline-flex items-center gap-2 border border-terracotta text-terracotta rounded-full px-5 py-2.5 text-sm hover:bg-terracotta/10 transition-colors disabled:opacity-50"
        >
          <TestTube2 size={15} /> Enviar test a mí
        </button>
        <button
          onClick={sendBroadcast}
          disabled={loading}
          data-testid="newsletter-send-broadcast-btn"
          className="inline-flex items-center gap-2 bg-charcoal text-ivory rounded-full px-6 py-2.5 text-sm font-medium hover:bg-terracotta transition-colors disabled:opacity-50"
        >
          {scheduledAt ? (
            <>
              <CalendarClock size={15} /> Programar para{" "}
              {new Date(scheduledAt).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
            </>
          ) : (
            <>
              <Send size={15} /> Enviar AHORA a {subscribersTotal} suscriptora{subscribersTotal === 1 ? "" : "s"}
            </>
          )}
        </button>
      </div>
    </div>
  </div>
);

export default NewsletterComposer;
